import React, { createContext, useState } from 'react';
import { Alert, AlertTitle, Box, Snackbar } from '@mui/material';

interface INotificationCenterProps {
  children: React.ReactElement;
}

type Status = 'success' | 'error' | 'warning' | 'info';
type Placement = 'topRight' | 'center';
export const NotificationCenterContext = createContext({
  pushNotification: (
    _message: string,
    _type?: Status,
    _title?: string,
    _placement?: Placement,
  ) => {},
});

interface IMessage {
  title?: string;
  message: string;
  type: Status;
  id: number;
  placement: Placement;
}

const NotificationCenter: React.FC<INotificationCenterProps> = ({
  children,
}) => {
  const [messages, setMessages] = useState<IMessage[]>([]);

  const handleClose = (id: number) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  return (
    <NotificationCenterContext.Provider
      value={{
        pushNotification: (message, type, title, placement = 'topRight') => {
          setMessages((prev) => [
            ...prev,
            {
              id: (prev[prev.length - 1]?.id || 0) + 1,
              message,
              title,
              type: type || 'success',
              placement,
            },
          ]);
        },
      }}
    >
      <>
        {children}
        <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 9999 }}>
          {messages.map((message) => {
            const close = () => handleClose(message.id);
            const isCentered = message.placement === 'center';

            return (
              <Snackbar
                autoHideDuration={5000}
                key={message.id}
                anchorOrigin={
                  isCentered
                    ? { vertical: 'top', horizontal: 'center' }
                    : { vertical: 'top', horizontal: 'right' }
                }
                open={true}
                onClose={(
                  event: React.SyntheticEvent | Event,
                  reason?: string,
                ) => {
                  if (reason === 'clickaway') {
                    return;
                  }

                  close();
                }}
                onClick={close}
                message={
                  <Alert sx={{ width: '100%' }} severity={message.type}>
                    {message.title && <AlertTitle>{message.title}</AlertTitle>}
                    {message.message}
                  </Alert>
                }
                sx={{
                  position: isCentered ? 'fixed' : 'relative',
                  ...(isCentered
                    ? {
                        bottom: 'auto !important',
                        left: '50% !important',
                        right: 'auto !important',
                        top: '50% !important',
                        transform: 'translate(-50%, -50%) !important',
                      }
                    : {}),
                  mb: 2,
                  padding: 0,
                  cursor: 'pointer',
                  '.MuiSnackbarContent-root': {
                    backgroundColor: 'white',
                    color: '#323232',
                    padding: 0,
                  },
                  '.MuiSnackbarContent-message': {
                    padding: 0,
                    width: '100%',
                  },
                }}
              />
            );
          })}
        </Box>
      </>
    </NotificationCenterContext.Provider>
  );
};

export default NotificationCenter;
